import { io, Socket } from 'socket.io-client';
import { SOCKET_URL, SITE_NUMBER } from 'src/config-global';

type EventCallback = (...args: any[]) => void;

class SocketService {
  private socket: Socket | null = null;

  private siteNumber: number = SITE_NUMBER;

  private eventHandlers: Map<string, Set<EventCallback>> = new Map();

  private isInitialized: boolean = false;

  /**
   * Initialize socket connection
   */
  public initialize(): void {
    if (this.isInitialized && this.socket?.connected) {
      console.log('Socket already initialized');
      return;
    }

    this.socket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
      autoConnect: true,
    });

    this.setupSocketListeners();

    // Attach any pending event listeners that were registered before socket was initialized
    this.eventHandlers.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.socket?.on(event, callback);
      });
    });

    this.isInitialized = true;
  }

  /**
   * Setup default socket event listeners
   */
  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id);
      this.joinSiteRoom();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      this.joinSiteRoom();
    });
  }

  /**
   * Register as a new player
   */
  public registerPlayer(user: any): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('Socket not connected, cannot register player');
      return;
    }

    this.socket.emit('newPlayer', {
      ...user,
      siteNumber: this.siteNumber,
    });

    console.log('Player registered with site number:', this.siteNumber);
  }

  /**
   * Join site-specific room
   */
  public joinSiteRoom(): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('Socket not connected, cannot join site room');
      return;
    }

    this.socket.emit('joinSiteRoom', { siteNumber: this.siteNumber });
    console.log(`Joined site room: site-${this.siteNumber}`);
  }

  /**
   * Subscribe to an event
   */
  public on(event: string, callback: EventCallback): void {
    // Store callback for cleanup (even if socket not ready yet)
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)?.add(callback);

    // Add socket listener if socket exists
    if (this.socket) {
      this.socket.on(event, callback);
      console.log(`[SocketService] Listener attached for event: ${event}`);
    } else {
      console.warn(
        `[SocketService] Socket not initialized yet, callback stored for event: ${event}. Will attach when socket is ready.`
      );
      // If socket gets initialized later, we need to attach the listener
      // This will be handled when initialize() is called
    }
  }

  /**
   * Unsubscribe from an event
   */
  public off(event: string, callback?: EventCallback): void {
    if (!this.socket) {
      return;
    }

    if (callback) {
      // Remove specific callback
      this.socket.off(event, callback);
      this.eventHandlers.get(event)?.delete(callback);
    } else {
      // Remove all callbacks for this event
      const callbacks = this.eventHandlers.get(event);
      if (callbacks) {
        callbacks.forEach((cb) => {
          this.socket?.off(event, cb);
        });
        callbacks.clear();
      }
      this.eventHandlers.delete(event);
    }
  }

  /**
   * Emit an event
   */
  public emit(event: string, data?: any): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('Socket not connected, cannot emit event:', event);
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Get socket instance
   */
  public getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Check if socket is connected
   */
  public isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get current site number
   */
  public getSiteNumber(): number {
    return this.siteNumber;
  }

  /**
   * Disconnect socket
   */
  public disconnect(): void {
    if (this.socket) {
      // Remove all event listeners
      this.eventHandlers.forEach((callbacks, event) => {
        callbacks.forEach((callback) => {
          this.socket?.off(event, callback);
        });
      });
      this.eventHandlers.clear();

      this.socket.disconnect();
      this.socket = null;
      this.isInitialized = false;
      console.log('Socket disconnected');
    }
  }

  /**
   * Cleanup all listeners for a specific component/context
   */
  public cleanup(events: string[]): void {
    events.forEach((event) => {
      this.off(event);
    });
  }
}

// Export singleton instance
export const socketService = new SocketService();
